import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { push } from '@lagunovsky/redux-react-router'
import { useDispatch } from 'react-redux'
import i18n from 'simple-react-i18n'
import { Accordion, AccordionDetails, Grid, Tooltip } from '@mui/material'
import { ALIGN, NewTable } from '../../../components/datatable/NewTable'
import { AccordionTitle } from '../../../components/styled/Accordions'
import { STATION_NAME, STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from '../../../station/constants/StationConstants'
import { getSiteType } from '../../../utils/mapUtils/SiteTypes'
import { getNewStationTypeNameFromTypeCode } from '../../../utils/StationUtils'
import { exportFile } from '../../../utils/ExportDataUtil'
import { greyBlue, lightGrey } from '../../../utils/constants/ColorTheme'
import { getProductionUnitLabel, getStationLabel, getVolumesRows, toVolumesExportRows } from '../../utils/ProductionUnitVolumesUtils'
import { formatMilliers } from 'utils/StringUtil'

const STATION_COLUMN_WIDTH = 260

const SITE_TYPE_MARKERS = {
    [STATION_TYPE_CONSTANT.piezometry]: STATION_NAME.PIEZOMETER,
    [STATION_TYPE_CONSTANT.hydrometry]: STATION_NAME.HYDROMETRIC_STATION,
    [STATION_TYPE_CONSTANT.installation]: STATION_NAME.INSTALLATION,
}

// Clé de colonne "DD/MM/YYYY" | "ww/YYYY" | "MM/YYYY" | "YYYY" -> [période, année] (année absente en vue annuelle).
const splitYear = (column = '') => {
    const index = column.lastIndexOf('/')
    return index < 0 ? [column, undefined] : [column.slice(0, index), column.slice(index + 1)]
}

// En-tête sur deux lignes (période / année) pour bien séparer les colonnes. Pour alléger, l'année n'est
// affichée que sur la colonne la plus récente et sur la 1re période de chaque année (janvier, 1re semaine, 1er janvier).
// Les colonnes sont triées de la plus récente à la plus ancienne.
const getColumnHeader = (column, index, columns) => {
    const [period, year] = splitYear(column)
    if (!year) {
        return <div style={{ textAlign: 'right' }}>{column}</div>
    }
    const [, olderYear] = splitYear(columns[index + 1])
    const showYear = index === 0 || (!!olderYear && olderYear !== year)
    return (
        <div style={{ textAlign: 'right', lineHeight: 1.2 }}>
            <div>{period}</div>
            <div style={{ minHeight: '1.2em' }}>{showYear ? year : ''}</div>
        </div>
    )
}

const getStationCell = station => (
    <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{getStationLabel(station)}</span>}>
        <Grid container alignItems='center' spacing={1}>
            <img style={{ width: '1rem' }} src={getSiteType(SITE_TYPE_MARKERS[station.siteType])?.url} />
            {station.code || station.name || station.siteCode}
        </Grid>
    </Tooltip>
)

// Tableau transposé d'une unité de production : une ligne par station, une colonne par période.
const ProductionUnitVolumesTable = ({
    productionUnit = {},
    stationsIn = [],
    stationsOut = [],
    measures = [],
    columns = [],
    formatDate = () => '',
    expanded = false,
    onToggle = () => {},
    occurenceFilter,
}) => {
    const dispatch = useDispatch()

    const onClickRow = useCallback(({ linkedStation }) => {
        if (linkedStation) {
            dispatch(push(`/station/${getNewStationTypeNameFromTypeCode(linkedStation.siteType)}/${linkedStation.siteCode}`))
        }
    }, [dispatch])

    const volumesRows = useMemo(
        () => getVolumesRows({ stationsIn, stationsOut, measures, columns, formatDate }),
        [columns, formatDate, measures, stationsIn, stationsOut],
    )

    const headers = useMemo(() => [
        { key: 'station', value: i18n.station, noSort: true, sticky: ALIGN.LEFT, width: STATION_COLUMN_WIDTH },
        ...columns.map((column, index) => ({ key: column, value: getColumnHeader(column, index, columns), noSort: true })),
    ], [columns])

    const rows = useMemo(() => volumesRows.map(row => ({
        station: row.isTotal ? { value: row.label, color: lightGrey } : getStationCell(row.station),
        ...(row.isTotal
            ? columns.reduce((acc, column) => ({ ...acc, [column]: { value: formatMilliers(row.values[column], true), color: lightGrey, align: ALIGN.RIGHT } }), {})
            : columns.reduce((acc, column) => ({ ...acc, [column]: { value: row.values[column], align: ALIGN.RIGHT } }), {})),
        linkedStation: row.station,
    })), [columns, volumesRows])

    return (
        <Accordion
            expanded={expanded}
            onChange={(_, value) => onToggle(productionUnit.id, value)}
            sx={{ marginBottom: '1rem', '&.Mui-expanded': { marginBottom: '1rem' }, '&::before': { display: 'none' } }}
        >
            <AccordionTitle
                color={greyBlue}
                title={getProductionUnitLabel(productionUnit)}
                actions={[
                    {
                        icon: 'launch',
                        tooltip: i18n.seeProductionUnit,
                        color: 'white',
                        onClick: () => dispatch(push(`/station/${STATION_TYPE_NAME.productionUnit}/${productionUnit.id}/dashboard`, { occurenceFilter })),
                    },
                    {
                        icon: 'file_download',
                        tooltip: i18n.export,
                        color: 'white',
                        onClick: () => exportFile({
                            data: toVolumesExportRows(volumesRows, columns),
                            exportType: 'xlsx',
                            titleFile: `${i18n.volumes} ${productionUnit.code || productionUnit.id}`,
                        }),
                    },
                ]}
            />
            <AccordionDetails>
                <NewTable
                    rows={rows}
                    headers={headers}
                    rowsPerPageOptions={[{ value: 1000, label: '1000' }]}
                    onClickRow={onClickRow}
                />
            </AccordionDetails>
        </Accordion>
    )
}

ProductionUnitVolumesTable.propTypes = {
    productionUnit: PropTypes.shape({
        id: PropTypes.number,
        code: PropTypes.string,
        name: PropTypes.string,
    }),
    stationsIn: PropTypes.arrayOf(PropTypes.shape({})),
    stationsOut: PropTypes.arrayOf(PropTypes.shape({})),
    measures: PropTypes.arrayOf(PropTypes.shape({})),
    columns: PropTypes.arrayOf(PropTypes.string),
    formatDate: PropTypes.func,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    occurenceFilter: PropTypes.shape({
        occurence: PropTypes.string,
        nbOccurence: PropTypes.number,
    }),
}

export default ProductionUnitVolumesTable
